import React, { useState, ReactElement, useEffect } from 'react';
import {  CircularProgress, Modal } from '@mui/material';
import { NextButton, SkipButton } from '../../styles/PersonalInfoStep';
import { GET } from '../../services/HTTPServices';
import { CustomTextArea, ErrorText, LoadingContainer, LoadingText, ModalBody, ModalContainer, ModalFooter, ModalHeader, ReasonItem, StyledCheckbox, StyledFormControlLabel } from '../../styles/DeleteModal';
import { ModalBodyText, ModalHeaderText } from '../../styles/modalTypography';

interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reasons: string[], otherReason?: string) => Promise<void>;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ open, onClose, onConfirm }) => {
  const [step, setStep] = useState<'reasons' | 'confirm' | 'processing' | 'success'>('reasons');
  const [selectedReasons, setSelectedReasons] = useState<any[]>([]);
  const [otherReason, setOtherReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasons, setReasons] = useState<any[]>([]);
  const [showError, setShowError] = useState(false);

    useEffect(() => {
      getDataReasons()
    }, [])
  
      const getDataReasons = async () => {
        let res: any = await GET(
          "dereason/GetDeReasonByType?type=2","provider"
        );
        if (res && res.data) {
          setReasons(res.data)
        }
      };

  const handleReasonToggle = (reason: string) => {
    setSelectedReasons(prev => 
      prev.includes(reason)
        ? prev.filter(r => r !== reason)
        : [...prev, reason]
    );
  };

  const handleNext = () => {
    const isOthersSelected = selectedReasons.some((r) => r.id === 16);
    const isInvalid = selectedReasons.length === 0 || (isOthersSelected && !otherReason.trim());

    if (isInvalid) {
      setShowError(true);
    } else {
      setShowError(false);
      setStep('confirm');
    }
  };

  const handleBack = () => {

    if (step === 'confirm') {
      onClose();
    } else if (step === 'reasons') {
      setSelectedReasons([])
      onClose();
    } 
  };

  const handleSubmit = async () => {
    try {
      // setIsSubmitting(true);
      setStep('processing');
      
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      await onConfirm(selectedReasons, otherReason);
      setStep('success');
      
      // Auto-close after showing success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      // Handle error state if needed
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderReasonsStep = (): ReactElement => (
    <>
      <ModalHeader>
        <ModalHeaderText>We're sorry to see you go.</ModalHeaderText>
      </ModalHeader>
      <ModalBody>
        <ModalBodyText>Please let us know why you're leaving:</ModalBodyText>
        
        {reasons.map((reason) => (
          <ReasonItem key={reason.id}>
            <StyledFormControlLabel
              control={
                <StyledCheckbox
                  checked={selectedReasons.findIndex((res : any)=> res.id === reason.id) > -1}
                  onChange={() => handleReasonToggle(reason)}
                />
              }
              label={                      <ModalBodyText $margin="-4px 8px 0px 0px">
              {reason.reason}</ModalBodyText>}
            />
          </ReasonItem>
        ))}
        
        
        {(selectedReasons.findIndex((res : any)=> res.id === 16) > -1) && (
          <CustomTextArea
            multiline
            rows={4}
            variant="outlined"
            placeholder="Please specify..."
            value={otherReason}
            onChange={(e) => setOtherReason(e.target.value)}
          />
        )}
        
        {showError && (selectedReasons.length === 0 || (selectedReasons.some(r => r.id === 16) && !otherReason.trim())) && (
          <ErrorText>Please select at least one reason or provide details for 'Others'</ErrorText>
        )}
      </ModalBody>
      <ModalFooter>
        <SkipButton onClick={handleBack}>
          Cancel
        </SkipButton>
        <NextButton 
          onClick={handleNext}
        >
          Continue
        </NextButton>
      </ModalFooter>
    </>
  );

  const renderConfirmStep = (): ReactElement => (
    <>
      <ModalHeader>
        <ModalHeaderText>Are you sure?</ModalHeaderText>
      </ModalHeader>
      <ModalBody>
        <ModalBodyText>This action cannot be undone. All your data will be permanently deleted.</ModalBodyText>
      </ModalBody>
      <ModalFooter>
        <SkipButton onClick={handleBack}>
          Cancel
        </SkipButton>
        <NextButton 
          onClick={handleSubmit}
          disabled={isSubmitting}
          $bgColor='#d32f2f'
        >
          Confirm Delete
        </NextButton>
      </ModalFooter>
    </>
  );

  const renderLoadingStep = (): ReactElement => (
    <LoadingContainer>
      <CircularProgress />
      <LoadingText>Deleting your account...</LoadingText>
    </LoadingContainer>
  );

  const renderSuccessStep = (): ReactElement => (
    <>
      <ModalHeader>
        <ModalHeaderText>Account Deleted</ModalHeaderText>
      </ModalHeader>
      <ModalBody>
        <ModalBodyText>Your account has been successfully deleted. We're sorry to see you go.</ModalBodyText>
        <ModalBodyText>Thank you for using our service.</ModalBodyText>
      </ModalBody>
      <ModalFooter>
        <NextButton onClick={onClose} $width="100%">
          Close
        </NextButton>
      </ModalFooter>
    </>
  );

  const renderStep = () => {
    switch (step) {
      case 'reasons':
        return renderReasonsStep();
      case 'confirm':
        return renderConfirmStep();
      case 'processing':
        return renderLoadingStep();
      case 'success':
        return renderSuccessStep();
      default:
        return null;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        {renderStep()}
      </ModalContainer>
    </Modal>
  );
};

export default DeleteAccountModal;
